import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  listThemes, createTheme, setThemeDefault, archiveTheme, themeUsage,
  type CmsScope, type CmsTheme, type ThemeUsage,
} from '../../lib/cmsAdmin';
import { listOrganizations, type Organization } from '../../lib/tenancy';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useAdminScope } from '../../admin/scope';
import './AdminPages.css';
import './AdminCms.css';

/**
 * Themes: named bundles of design tokens.
 *
 * ── A THEME IS DATA, AND THAT IS THE PRODUCT DECISION ────────────────────────────────
 *
 * There is no CSS field on this screen or anywhere behind it. A theme sets a fixed list
 * of named values — eight colours, four typography choices, three radii, three layout
 * measures — and the product decides what each one means. A tenant cannot write a
 * selector, cannot load a font from elsewhere, and cannot reach a property Voleto did
 * not offer. The cost is that some designs are not expressible; the benefit is that
 * nothing published here can execute, and that every block picks up a rebrand without
 * being touched.
 */
export function AdminCmsThemes() {
  const { organizationId } = useAdminScope();
  const [themes, setThemes] = useState<CmsTheme[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [reload, setReload] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);

  usePageChrome({
    title: 'Themes',
    subtitle: 'Colours, type, shape and layout, as named tokens. Publishing a theme '
      + 'restyles every page that resolves it.',
  });

  useEffect(() => {
    setThemes(null);
    listThemes({ organizationId })
      .then(setThemes)
      .catch((err: unknown) => setError(err instanceof ApiError
        ? err.message : 'Themes could not be loaded.'));
  }, [organizationId, reload]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return themes ?? [];
    return (themes ?? []).filter((t) => t.name.toLowerCase().includes(term));
  }, [themes, search]);

  async function toggleDefault(theme: CmsTheme) {
    const making = !theme.isDefault;
    if (making && !window.confirm(
      `Make "${theme.name}" the default for ${theme.scope === 'platform'
        ? 'the whole platform' : 'this organization'}? Every page that inherits will `
      + 'start using it.',
    )) return;
    setBusyId(theme.id);
    try {
      await setThemeDefault(theme.id, making);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be changed.');
    } finally { setBusyId(null); }
  }

  async function archive(theme: CmsTheme) {
    if (!window.confirm(
      `Archive "${theme.name}"? The server refuses this while it is still assigned, and `
      + 'refuses it outright for the only platform default.',
    )) return;
    setBusyId(theme.id);
    try {
      await archiveTheme(theme.id);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That theme could not be archived.');
    } finally { setBusyId(null); }
  }

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <div className="adm-filters">
        <input className="adm-select" type="search" placeholder="Search themes…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          aria-label="Search themes" />
        <Link className="button button--primary" to="/admin/content/themes/new">New theme</Link>
      </div>

      {!themes && !error && <p className="adm-lede">Loading themes…</p>}
      {themes && shown.length === 0 && (
        <p className="adm-lede">No themes {search ? 'match that search' : 'yet'}.</p>
      )}

      {shown.length > 0 && (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Name</th><th>Scope</th><th>Live</th><th>Draft</th>
              <th>Used by</th><th>Created</th><th />
            </tr>
          </thead>
          <tbody>
            {shown.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/admin/content/themes/${t.id}`}>{t.name}</Link>
                  {t.isDefault && <span className="badge badge--ok">Default</span>}
                  {t.description && <div className="adm-muted">{t.description}</div>}
                </td>
                <td>{t.scope === 'platform' ? 'Platform' : 'Organization'}</td>
                <td>
                  {t.publishedVersion
                    ? `v${t.publishedVersion}`
                    : <span className="adm-muted">never</span>}
                </td>
                <td>
                  {t.draftVersion
                    ? <span className="badge badge--pending">v{t.draftVersion}</span>
                    : '—'}
                </td>
                <td><UsedBy theme={t} /></td>
                <td className="adm-nowrap">{new Date(t.createdAt).toLocaleDateString()}</td>
                <td className="adm-actions">
                  <button type="button" className="button button--ghost adm-btn-sm"
                    disabled={busyId === t.id} onClick={() => { void toggleDefault(t); }}>
                    {t.isDefault ? 'Unset default' : 'Make default'}
                  </button>
                  <button type="button" className="button button--ghost adm-btn-sm"
                    disabled={busyId === t.id} onClick={() => { void archive(t); }}>
                    Archive
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** How many real pages this theme is responsible for. */
function UsedBy({ theme }: { theme: CmsTheme }) {
  const [usage, setUsage] = useState<ThemeUsage | null>(null);

  useEffect(() => {
    let cancelled = false;
    themeUsage(theme.id)
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { /* The list works without the count. */ });
    return () => { cancelled = true; };
  }, [theme.id]);

  if (!usage) return <span className="adm-muted">…</span>;
  const assigned = usage.templates + usage.pages + usage.organizations;
  if (assigned === 0 && usage.events === 0) return <span className="adm-muted">nothing yet</span>;
  return (
    <span title={`${usage.templates} template(s), ${usage.pages} page(s), `
      + `${usage.organizations} organization default(s).`}>
      {usage.events > 0
        ? `${usage.events} event${usage.events === 1 ? '' : 's'}`
        : `${assigned} assignment${assigned === 1 ? '' : 's'}`}
    </span>
  );
}

/** Create a theme. It starts from the platform values and is edited from there. */
export function AdminCmsThemeNew() {
  const navigate = useNavigate();
  const { organizationId } = useAdminScope();

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [scope, setScope] = useState<CmsScope>(organizationId ? 'organization' : 'platform');
  const [org, setOrg] = useState(organizationId ?? '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePageChrome({
    title: 'New theme',
    breadcrumb: [
      { label: 'Content & design' },
      { label: 'Themes', to: '/admin/content/themes' },
      { label: 'New' },
    ],
  });

  useEffect(() => { listOrganizations().then(setOrgs).catch(() => setOrgs([])); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await createTheme({
        scope,
        organizationId: scope === 'organization' ? (org || null) : null,
        name: name.trim(),
        description: description.trim() || null,
      });
      navigate(`/admin/content/themes/${created.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The theme could not be created.');
      setBusy(false);
    }
  }

  return (
    <div className="adm-page">
      <form className="adm-form cms-form" onSubmit={submit}>
        {error && <div className="alert alert--error" role="alert">{error}</div>}

        <label className="field">
          <span className="field__label">Name</span>
          <input className="field__input" value={name} required maxLength={120}
            placeholder="Adams City brand" onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Description</span>
          <input className="field__input" value={description} maxLength={240}
            onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Owned by</span>
          <select className="field__input" value={scope}
            onChange={(e) => setScope(e.target.value as CmsScope)}>
            <option value="organization">An organization</option>
            <option value="platform">The platform</option>
          </select>
        </label>

        {scope === 'organization' && (
          <label className="field">
            <span className="field__label">Organization</span>
            <select className="field__input" value={org} required
              onChange={(e) => setOrg(e.target.value)}>
              <option value="">Choose one…</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
        )}

        <p className="adm-hint">
          A new theme starts from Voleto&rsquo;s own values. Change only what you need:
          anything you leave alone keeps inheriting, so a theme that changes one colour is
          one colour rather than a whole design system to maintain.
        </p>

        <div className="adm-actions">
          <button type="submit" className="button button--primary" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create and edit'}
          </button>
          <Link className="button button--ghost" to="/admin/content/themes">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
